import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  Divider,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material"; // Only using needed icons
import { toast } from "react-toastify";

import {
  useGetWhatsappConfigQuery,
  useWhatsappConfigMutation,
  useUpdateWhatsappConfigMutation,
} from "../../../Services/inventory/Inventory";

const ORANGE = "#ff9800";
const ORANGE_LIGHT = "#fff3e0";
const API_VERSION = "v17.0";

const WhatsAppConfig = () => {
  const {
    data: whatsappConfigResponse,
    isLoading,
    isError,
    refetch,
  } = useGetWhatsappConfigQuery({});

  const [createWhatsappConfig] = useWhatsappConfigMutation();
  const [updateWhatsappConfig] = useUpdateWhatsappConfigMutation();

  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    id: 0,
    business_id: "",
    phone_number_id: "",
    user_access_token: "",
    api_version: API_VERSION,
  });

  const [originalFormData, setOriginalFormData] = useState({
    id: 0,
    business_id: "",
    phone_number_id: "",
    user_access_token: "",
    api_version: API_VERSION,
  });

  useEffect(() => {
    if (whatsappConfigResponse?.data) {
      setData([whatsappConfigResponse.data]);
    } else {
      setData([]);
    }
  }, [whatsappConfigResponse]);

  const handleOpenModal = (config?: any) => {
    if (config) {
      const formattedConfig = { ...config, api_version: "v22.0" }; // Ensure static api_version
      setFormData(formattedConfig);
      setOriginalFormData(formattedConfig); // Store original data for comparison
    } else {
      const newConfig = {
        id: 0,
        business_id: "",
        phone_number_id: "",
        api_version: "v22.0",
        user_access_token: "",
      };
      setFormData(newConfig);
      setOriginalFormData(newConfig); // Store original data for comparison
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const hasFormChanged = () => {
    // Exclude id and api_version for modification detection
    const { id: id1, api_version: av1, ...curr } = formData;
    const { id: id2, api_version: av2, ...orig } = originalFormData;
    return JSON.stringify(curr) !== JSON.stringify(orig);
  };

  const isFormValid = () => {
    const { business_id, phone_number_id, user_access_token } = formData;
    return (
      business_id?.trim() !== "" &&
      phone_number_id?.trim() !== "" &&
      user_access_token?.trim() !== ""
    );
  };

  const isSaveEnabled = () => {
    const isValid = isFormValid();
    const isNewConfig = !formData.id || formData.id === 0;
    const hasChanges = hasFormChanged();
    return isValid && (isNewConfig || hasChanges);
  };

  // Check if Add button should be disabled
  const isAddButtonDisabled = () => {
    return isLoading || data.length > 0; // Disable if loading or data exists
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        business_id: formData.business_id,
        phone_number_id: formData.phone_number_id,
        user_access_token: formData.user_access_token,
        api_version: API_VERSION, // Always
      };
      if (formData.id && formData.id !== 0) {
        await updateWhatsappConfig({
          id: formData.id,
          body: payload,
        }).unwrap();
        toast.success("WhatsApp configuration updated successfully");
      } else {
        await createWhatsappConfig(payload).unwrap();
        toast.success("WhatsApp configuration created successfully");
      }
      await refetch();
      setIsModalOpen(false);
    } catch (error) {
      let message = "Failed to save WhatsApp configuration";
      if (error?.data?.error) message = error.data.error;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header and Add Button */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography
          variant="h6"
          fontWeight={200}
          style={{ fontFamily: "karbon", color: "#666666" }}
        >
          WhatsApp Configuration
        </Typography>
        {!isAddButtonDisabled() && (
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ color: "#fff" }} />}
            onClick={() => handleOpenModal()}
            style={{ fontFamily: "karbon" }}
            sx={{
              fontSize: 12,
              backgroundColor: ORANGE,
              textTransform: "none",
              fontWeight: 200,
              px: 3,
              py: 1.5,
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "#e65100",
              },
            }}
          >
            Add WhatsApp Config
          </Button>
        )}
      </Box>

      {/* Table Display */}
      {isError ? (
        <Typography color="error">Failed to load WhatsApp config</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead
              style={{
                backgroundColor: ORANGE_LIGHT,
                borderBottom: `2px solid ${ORANGE}`,
                fontFamily: "karbon",
                color: "#666666",
                fontWeight: 100,
              }}
            >
              <tr>
                <th
                  style={{
                    padding: "12px",
                    fontWeight: 100,
                    textAlign: "left",
                    color: ORANGE,
                  }}
                >
                  Business ID
                </th>
                <th
                  style={{
                    padding: "12px",
                    fontWeight: 100,
                    textAlign: "left",
                    color: ORANGE,
                  }}
                >
                  Phone Number ID
                </th>
                <th
                  style={{
                    padding: "12px",
                    fontWeight: 100,
                    textAlign: "left",
                    color: ORANGE,
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#777",
                    }}
                  >
                    No configurations found. Add your first WhatsApp
                    configuration.
                  </td>
                </tr>
              ) : (
                data.map((row) => (
                  <tr
                    key={row.id || row.business_id}
                    style={{
                      cursor: "default",
                      fontFamily: "karbon",
                      color: "#666666",
                      fontWeight: 100,
                    }}
                  >
                    <td style={{ padding: "10px" }}>
                      {row.business_id || "-"}
                    </td>
                    <td style={{ padding: "10px" }}>
                      {row.phone_number_id || "-"}
                    </td>
                    <td style={{ padding: "10px" }}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            sx={{ color: ORANGE }}
                            onClick={() => handleOpenModal(row)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* Delete functionality hidden/commented out */}
                      </Box>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        disableEscapeKeyDown={loading}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            boxShadow: 24,
            borderRadius: 2,
            maxHeight: "90vh",
            overflowY: "auto",
            width: 400,
            p: 3,
          }}
        >
          <Typography
            variant="h6"
            mb={2}
            style={{ fontFamily: "karbon-medium", fontSize: "16px" }}
          >
            {formData.id
              ? "Edit WhatsApp Configuration"
              : "Add WhatsApp Configuration"}
          </Typography>

          <Grid container spacing={2}>
            {/* Business ID */}
            <Grid item xs={12}>
              <Typography
                variant="body2"
                fontWeight={100}
                mb={1}
                className="required"
                style={{ fontFamily: "karbon", color: "#666666" }}
              >
                Business ID
              </Typography>
              <TextField
                name="business_id"
                value={formData.business_id}
                onChange={handleInputChange}
                fullWidth
                disabled={loading}
                required
                placeholder="Enter business ID"
                style={{ fontFamily: "karbon", color: "#666666" }}
              />
            </Grid>

            {/* Phone Number ID */}
            <Grid item xs={12}>
              <Typography
                variant="body2"
                fontWeight={500}
                mb={1}
                className="required"
                style={{ fontFamily: "karbon", color: "#666666" }}
              >
                Phone Number ID
              </Typography>
              <TextField
                name="phone_number_id"
                value={formData.phone_number_id}
                onChange={handleInputChange}
                fullWidth
                disabled={loading}
                required
                placeholder="Enter phone number ID"
                style={{ fontFamily: "karbon", color: "#666666" }}
              />
            </Grid>

            {/* User Access Token */}
            <Grid item xs={12}>
              <Typography
                variant="body2"
                fontWeight={100}
                mb={1}
                className="required"
                style={{ fontFamily: "karbon", color: "#666666" }}
              >
                User Access Token
              </Typography>
              <TextField
                name="user_access_token"
                type="password"
                value={formData.user_access_token}
                onChange={handleInputChange}
                fullWidth
                disabled={loading}
                required
                placeholder="Enter user access token"
                style={{ fontFamily: "karbon", color: "#666666" }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={handleCloseModal}
              sx={{
                textTransform: "none",
                px: 3,
                color: ORANGE,
                borderColor: ORANGE,
                "&:hover": {
                  backgroundColor: ORANGE_LIGHT,
                  borderColor: ORANGE,
                },
              }}
              disabled={loading}
              style={{ fontFamily: "karbon" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || !isSaveEnabled()}
              style={{ fontFamily: "karbon" }}
              sx={{
                textTransform: "none",
                px: 3,
                backgroundColor: ORANGE,
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#e65100",
                },
              }}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default WhatsAppConfig;
